// MARK://dhk8/pos/pages/TakeoutOrdersPage
// "Pedidos para llevar" (/para-llevar) — VISTA DE COLA.
//
// Decisión de diseño (item del coordinador):
//   • /floor es por MESA — plano físico, comensales por mesa, ocupación.
//   • /para-llevar es por PERSONAS EN COLA — no hay mesas, sólo espera.
// La pantalla DEBE verse y manejarse diferente del plano. Aquí no hay
// canvas espacial: hay una cola visual, con un conteo prominente de cuántas
// personas/ordenes están esperando y cada orden como un boleto numerado.
//
// Flujo conservado (item C de OhSir, no se rompe):
//   • Las órdenes TAKEOUT del QR llegan OPEN sin pasar a cocina.
//   • El staff cobra en mostrador y pulsa "Confirmar pago" → dhk8-pos-orders
//     registra el pago y dispara la comanda a KDS. Sólo entonces se prepara.
//   • Los pedidos de mesa (DINE_IN) NO aparecen aquí.
//
// Polling cada 10 s (no hay gateway WS todavía).
//
// Para que el contraste visual con /floor sea claro:
//   • HERO con el conteo grande "X personas esperando" (cifra dominante).
//   • Lista en columna vertical tipo cola/queue (boletos con número de
//     turno #1, #2, #3...).
//   • Sin color verde de plano: la cola usa el ámbar (pendiente de cobro)
//     como hilo conductor, y el verde sólo para la acción "enviar a cocina".

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingBag, RefreshCw, Check, Clock, ChevronRight, Ban, Pencil } from 'lucide-react'

import {
  listOrders,
  recordOrderPayment,
  cancelOrder,
  type BackendOrderDto,
} from '../lib/clients/orders.js'
import { getTenantId } from '../lib/api.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { useAuthStore } from '../stores/auth-store.js'
import { requireManagerAuth } from '../features/security/pin-gate-store.js'
import { PickupDeliveryPanel } from '../features/pos/PickupDeliveryPanel.js'
import { formatCents } from '../lib/money.js'

const POLL_MS = 10_000

function fmtTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' }).format(d)
}

function elapsedMin(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000))
}

function fmtElapsed(iso: string): string {
  const mins = elapsedMin(iso)
  if (mins < 1) return 'ahora'
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

/** Identificador del pedido que el staff y el comensal comparten. SIEMPRE del
 *  servidor: el código de recolección si lo trae, si no el folio real de la orden.
 *  (Antes esto era `orderId.slice(0,8)` — un folio inventado en el cliente que el
 *  comensal veía pero que aquí no se podía validar contra nada.) */
function orderRef(o: BackendOrderDto): string {
  return o.pickupCode ?? o.folio
}

/** ¿El pedido sigue "por cobrar"? (define quién entra a la cola y quién muestra
 *  las acciones de cobro en la vista Todos). OPEN con ítems sin disparar, o
 *  PARTIALLY_PAID (barra pago-mixto a medias). */
function isPayable(o: BackendOrderDto): boolean {
  return o.status === 'PARTIALLY_PAID' || (o.status === 'OPEN' && o.items.some((it) => it.status === 'PENDING'))
}

/** Estatus del pedido para llevar → etiqueta + clase de color (vista Todos). */
function statusMeta(o: BackendOrderDto): { label: string; cls: string } {
  switch (o.status) {
    case 'OPEN': return isPayable(o) ? { label: 'Por cobrar', cls: 'wait' } : { label: 'En cocina', cls: 'open' }
    case 'PARTIALLY_PAID': return { label: 'Pago parcial', cls: 'wait' }
    case 'SENT_TO_KITCHEN': return { label: 'En cocina', cls: 'open' }
    case 'CLOSED': return o.deliveredAt ? { label: 'Entregado', cls: 'done' } : { label: 'Pagado', cls: 'done' }
    case 'CANCELLED': return { label: 'Cancelado', cls: 'muted' }
    case 'REJECTED': return { label: 'Rechazado', cls: 'danger' }
    case 'PENDING_ACCEPTANCE': return { label: 'Por aceptar', cls: 'wait' }
    default: return { label: o.status, cls: 'muted' }
  }
}

/** Wait-tier color cue. Cuanto más tiempo, más urgente — pero suave (no
 *  rojo agresivo): la cola se acumula, no es un error. */
function waitTier(mins: number): 'fresh' | 'wait' | 'long' {
  if (mins < 5) return 'fresh'
  if (mins < 15) return 'wait'
  return 'long'
}

export default function TakeoutOrdersPage() {
  const navigate = useNavigate()
  const tenantId = useTenantId()
  const user = useAuthStore((s) => s.user)
  const [orders, setOrders] = useState<BackendOrderDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [doneCount, setDoneCount] = useState(0)
  // Vista: cola de cobro (default) vs TODOS los pedidos para llevar (dimensionar
  // cuando llegan muchos: ver también los ya en cocina / pagados / entregados).
  const [view, setView] = useState<'queue' | 'all'>('queue')

  const reload = useCallback(async () => {
    const tid = getTenantId()
    if (!tid) {
      setError('No hay tenant activo.')
      setLoading(false)
      return
    }
    try {
      setError(null)
      if (view === 'all') {
        // Todos los pedidos para llevar recientes, cualquier estatus (el grafo
        // `orders` sin filtro de status los trae todos). Más nuevo primero.
        const res = await listOrders({ tenantId: tid, limit: 100 })
        const takeout = res.items
          .filter((o) => o.type === 'TAKEOUT')
          .sort((a, b) => b.openedAt.localeCompare(a.openedAt))
        setOrders(takeout)
      } else {
        // Cola por cobrar: OPEN (el pago las cierra + dispara a cocina) Y
        // PARTIALLY_PAID (barra pago-mixto abandonado a medias — falta el saldo;
        // se recuperan aquí para completar el cobro). Filtramos type=TAKEOUT.
        const [openRes, partialRes] = await Promise.all([
          listOrders({ tenantId: tid, status: 'OPEN', limit: 100 }),
          listOrders({ tenantId: tid, status: 'PARTIALLY_PAID', limit: 100 }),
        ])
        const takeout = [...openRes.items, ...partialRes.items]
          .filter((o) => o.type === 'TAKEOUT')
          .filter(isPayable)
          .sort((a, b) => a.openedAt.localeCompare(b.openedAt))
        setOrders(takeout)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar los pedidos para llevar')
    } finally {
      setLoading(false)
    }
  }, [view])

  useEffect(() => {
    setLoading(true)
    void reload()
    const iv = setInterval(() => void reload(), POLL_MS)
    return () => clearInterval(iv)
    // tenantId/reload en deps: cambio de entorno (Test↔Live) o de vista refresca.
  }, [tenantId, reload])

  /** Confirma el pago de un pedido para llevar → dhk8-pos-orders lo dispara a
   *  cocina (KDS). El pago es del total completo, método efectivo (mostrador).
   *  Comportamiento idéntico al de la versión anterior — sólo cambió el look. */
  async function confirmPayment(o: BackendOrderDto) {
    setBusyId(o.id)
    try {
      // Paga el RESTANTE (no el total): una orden PARTIALLY_PAID de barra ya trae
      // pagos previos — cobrar el total de nuevo la SOBRE-pagaría (money-path).
      const remainingCents = Math.max(0, o.totalCents - (o.paidCents ?? 0))
      await recordOrderPayment(o.id, {
        amountCents: remainingCents,
        method: 'CASH',
        capturedById: user?.id ?? '00000000-0000-0000-0000-000000000001',
        reference: `Takeout ${orderRef(o)} — pago en mostrador`,
      })
      setDoneCount((n) => n + 1)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo confirmar el pago')
    } finally {
      setBusyId(null)
    }
  }

  /** Anular un pedido para llevar SIN cobrar (cliente no se presentó, pedido
   *  duplicado, etc.). Mismo gate que "Anular comanda" en OrderPage: manager-PIN
   *  + razón obligatoria → cancelOrder marca la orden CANCELLED (sale de la cola). */
  async function cancelTakeout(o: BackendOrderDto) {
    const auth = await requireManagerAuth('order.voidOrder', {
      requireReason: true,
      contextLabel: `Pedido para llevar ${orderRef(o)} — ${formatCents(o.totalCents)}`,
    })
    if (!auth.proceed) return
    const reason = auth.reason
    setBusyId(o.id)
    try {
      await cancelOrder(o.id, (reason ?? '').trim() || 'Pedido para llevar anulado', auth.managerAuthToken)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo anular el pedido')
    } finally {
      setBusyId(null)
    }
  }

  /** Editar un pedido para llevar → abre la comanda en /order para ajustar items
   *  antes de cobrar. */
  function editTakeout(o: BackendOrderDto) {
    navigate(`/order?orderId=${o.id}`)
  }

  // ── Cola: métricas agregadas (la "personalidad" de esta pantalla) ───────
  const queueStats = useMemo(() => {
    const waiting = orders.length
    if (waiting === 0) {
      return { waiting, longestWaitMin: 0, totalCents: 0, totalItems: 0 }
    }
    const longestWaitMin = orders.reduce((m, o) => Math.max(m, elapsedMin(o.openedAt)), 0)
    const totalCents = orders.reduce((s, o) => s + o.totalCents, 0)
    const totalItems = orders.reduce(
      (s, o) => s + o.items.filter((it) => it.status !== 'CANCELLED').reduce((n, it) => n + it.quantity, 0),
      0,
    )
    return { waiting, longestWaitMin, totalCents, totalItems }
  }, [orders])

  return (
    <div className="dhk8-tko-root">
      {/* ── HERO: el dato dominante es "X personas esperando" ──────────── */}
      <header className="dhk8-tko-hero">
        <div className="dhk8-tko-hero__top">
          <div>
            <div className="dhk8-tko-eyebrow">Punto de venta · Para llevar</div>
            <h1 className="dhk8-tko-hero__title">{view === 'all' ? 'Todos los pedidos' : 'Cola de pedidos'}</h1>
          </div>
          <ShoppingBag size={22} className="dhk8-tko-hero__icon" aria-hidden />
        </div>

        <div className="dhk8-tko-hero__big">
          <div className="dhk8-tko-hero__number" aria-live="polite">
            {queueStats.waiting}
          </div>
          <div className="dhk8-tko-hero__label">
            {view === 'all'
              ? (queueStats.waiting === 1 ? 'pedido para llevar' : 'pedidos para llevar')
              : (queueStats.waiting === 1 ? 'persona esperando' : 'personas esperando')}
          </div>
        </div>

        {queueStats.waiting > 0 && view === 'queue' && (
          <div className="dhk8-tko-hero__stats">
            <div className="dhk8-tko-stat">
              <Clock size={14} aria-hidden />
              <span className="dhk8-tko-stat__num">
                {queueStats.longestWaitMin === 0 ? '<1' : queueStats.longestWaitMin}
              </span>
              <span className="dhk8-tko-stat__unit">min · espera más larga</span>
            </div>
            <div className="dhk8-tko-stat">
              <span className="dhk8-tko-stat__num">{queueStats.totalItems}</span>
              <span className="dhk8-tko-stat__unit">artículos por preparar</span>
            </div>
            <div className="dhk8-tko-stat">
              <span className="dhk8-tko-stat__num">{formatCents(queueStats.totalCents)}</span>
              <span className="dhk8-tko-stat__unit">por cobrar</span>
            </div>
          </div>
        )}
      </header>

      {/* ── Entrega validada ────────────────────────────────────────────────
          Va aquí porque el mostrador es donde el cliente llega por su pedido:
          teclear el código y ver de quién es el pedido es parte del mismo puesto
          de trabajo que cobrar la cola. `reload` al entregar para que la cola
          refleje cualquier cambio de estado. */}
      <PickupDeliveryPanel onDelivered={() => void reload()} />

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="dhk8-tko-toolbar">
        <div className="dhk8-tko-seg" role="tablist" aria-label="Vista de pedidos para llevar">
          <button type="button" role="tab" aria-selected={view === 'queue'}
            className={`dhk8-tko-seg__btn ${view === 'queue' ? 'is-on' : ''}`}
            onClick={() => setView('queue')}>
            Cola
          </button>
          <button type="button" role="tab" aria-selected={view === 'all'}
            className={`dhk8-tko-seg__btn ${view === 'all' ? 'is-on' : ''}`}
            onClick={() => setView('all')}>
            Todos
          </button>
        </div>
        <button
          type="button"
          className="dhk8-tko-refresh"
          onClick={() => void reload()}
          title="Recargar"
        >
          <RefreshCw size={14} /> Recargar
        </button>
      </div>
      {view === 'queue' && (
        <span className="dhk8-tko-hint">
          {doneCount > 0
            ? `${doneCount} pedido(s) enviado(s) a cocina en esta sesión`
            : 'Cobra en el mostrador y confirma aquí para enviar a cocina'}
        </span>
      )}

      {error && <div className="dhk8-tko-err" role="alert">{error}</div>}
      {loading && <div className="dhk8-tko-empty">Cargando…</div>}
      {!loading && orders.length === 0 && !error && (
        <div className="dhk8-tko-empty dhk8-tko-empty--zen">
          <span className="dhk8-tko-empty__icon" aria-hidden>🍃</span>
          <span>{view === 'all' ? 'No hay pedidos para llevar recientes.' : 'Cola vacía. No hay pedidos para llevar por cobrar.'}</span>
        </div>
      )}

      {/* ── COLA: cada orden es un boleto con número de turno ──────────── */}
      <ol className="dhk8-tko-queue" aria-label="Cola de pedidos para llevar">
        {orders.map((o, idx) => {
          const mins = elapsedMin(o.openedAt)
          const tier = waitTier(mins)
          const turnNumber = idx + 1
          const payable = isPayable(o)
          const sm = statusMeta(o)
          return (
            <li key={o.id} className={`dhk8-tko-ticket dhk8-tko-ticket--${tier}`}>
              {/* Tira lateral con el número de turno (solo en la Cola) + línea */}
              <div className="dhk8-tko-ticket__rail" aria-hidden="true">
                {view === 'queue' && <span className="dhk8-tko-ticket__turn">#{turnNumber}</span>}
                <span className="dhk8-tko-ticket__rail-line" />
              </div>

              <div className="dhk8-tko-ticket__body">
                <div className="dhk8-tko-ticket__head">
                  <div className="dhk8-tko-ticket__head-left">
                    <strong className="dhk8-tko-ticket__folio">{orderRef(o)}</strong>
                    {view === 'all' ? (
                      <span className={`dhk8-tko-badge dhk8-tko-badge--${sm.cls}`}>{sm.label}</span>
                    ) : (
                      <span className={`dhk8-tko-ticket__wait dhk8-tko-ticket__wait--${tier}`}>
                        <Clock size={12} aria-hidden /> {fmtElapsed(o.openedAt)}
                      </span>
                    )}
                  </div>
                  <span className="dhk8-tko-ticket__when">{fmtTime(o.openedAt)}</span>
                </div>

                <ul className="dhk8-tko-items">
                  {o.items
                    .filter((it) => it.status !== 'CANCELLED')
                    .map((it) => (
                      <li key={it.id} className="dhk8-tko-item">
                        <span className="dhk8-tko-item__qty">{it.quantity}×</span>
                        <span className="dhk8-tko-item__name">
                          {it.name}
                          {it.modifiers.length > 0 && (
                            <span className="dhk8-tko-item__mods">
                              {' '}· {it.modifiers.map((m) => m.name).join(', ')}
                            </span>
                          )}
                          {it.notes && <span className="dhk8-tko-item__note"> · {it.notes}</span>}
                        </span>
                        <span className="dhk8-tko-item__price">{formatCents(it.totalCents)}</span>
                      </li>
                    ))}
                </ul>

                <div className="dhk8-tko-ticket__foot">
                  <div className="dhk8-tko-total">
                    {/* Lineas NETAS: sin Subtotal/IVA el Total a cobrar no cuadraba a la vista. */}
                    {o.taxCents > 0 && (
                      <span className="dhk8-tko-total__break">
                        Subtotal {formatCents(o.subtotalCents)} · IVA {formatCents(o.taxCents)}
                      </span>
                    )}
                    <span className="dhk8-tko-total__label">{payable ? 'Total a cobrar' : 'Total'}</span>
                    <strong className="dhk8-tko-total__amount">{formatCents(o.totalCents)}</strong>
                  </div>
                  {payable && (
                  <div className="dhk8-tko-ticket__actions">
                    <button
                      type="button"
                      className="dhk8-tko-secondary"
                      disabled={busyId === o.id}
                      onClick={() => editTakeout(o)}
                      title="Editar la comanda antes de cobrar"
                    >
                      <Pencil size={14} /> Editar
                    </button>
                    <button
                      type="button"
                      className="dhk8-tko-secondary dhk8-tko-secondary--danger"
                      disabled={busyId === o.id}
                      onClick={() => void cancelTakeout(o)}
                      title="Anular el pedido sin cobrar (requiere gerente)"
                    >
                      <Ban size={14} /> Anular
                    </button>
                    <button
                      type="button"
                      className="dhk8-tko-pay"
                      disabled={busyId === o.id}
                      onClick={() => void confirmPayment(o)}
                      title="Confirma el pago en efectivo y dispara la orden a cocina"
                    >
                      {busyId === o.id ? (
                        <>Confirmando…</>
                      ) : (
                        <>
                          <Check size={15} /> Pagado <ChevronRight size={15} /> a cocina
                        </>
                      )}
                    </button>
                  </div>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ol>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  /* NintaiGO design rules:
       - NO border-radius (excepción: dots de estado — aquí no usamos ninguno).
       - NO box-shadow ni text-shadow. Elevación = borde 1 px o inversión bg/fg. */
  .dhk8-tko-root {
    display: flex; flex-direction: column; gap: var(--pos-space-4, 16px);
    padding: var(--pos-space-4, 16px); max-width: 880px;
  }

  /* ── HERO ────────────────────────────────────────────────────────────── */
  .dhk8-tko-hero {
    position: relative; overflow: hidden;
    background:
      linear-gradient(135deg, color-mix(in oklch, var(--pos-warning, #E0A23C) 16%, var(--pos-elevated, #1E2520)) 0%, var(--pos-surface, #161C18) 100%);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-left: 4px solid var(--pos-warning, #E0A23C);
    padding: var(--pos-space-5, 20px) var(--pos-space-5, 20px) var(--pos-space-4, 16px);
    display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
  }
  .dhk8-tko-hero__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .dhk8-tko-eyebrow {
    font-size: var(--pos-text-xxs, 11px); letter-spacing: .08em; text-transform: uppercase;
    color: var(--pos-fg-tertiary, #7E8C82); font-weight: var(--pos-weight-semibold, 600);
  }
  .dhk8-tko-hero__title {
    margin: 4px 0 0; font-size: var(--pos-text-xl, 20px); font-weight: var(--pos-weight-semibold, 600);
    color: var(--pos-fg, #E8EFE9); font-family: var(--font-display, system-ui);
  }
  .dhk8-tko-hero__icon { color: var(--pos-warning, #E0A23C); flex-shrink: 0; }

  .dhk8-tko-hero__big { display: flex; align-items: baseline; gap: var(--pos-space-3, 12px); }
  .dhk8-tko-hero__number {
    font-family: var(--font-display, system-ui);
    font-size: clamp(56px, 9vw, 96px);
    line-height: 1; font-weight: 800;
    color: var(--pos-fg, #E8EFE9);
    letter-spacing: -.02em;
    font-variant-numeric: tabular-nums;
  }
  .dhk8-tko-hero__label {
    font-size: var(--pos-text-lg, 17px); color: var(--pos-fg-secondary, #B6BFB8);
    font-weight: var(--pos-weight-medium, 500);
  }

  .dhk8-tko-hero__stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--pos-space-3, 12px);
    padding-top: var(--pos-space-3, 12px);
    border-top: 1px dashed var(--pos-separator, rgba(255,255,255,.10));
  }
  .dhk8-tko-stat {
    display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap;
    color: var(--pos-fg-secondary, #B6BFB8);
  }
  .dhk8-tko-stat svg { color: var(--pos-fg-tertiary, #7E8C82); align-self: center; }
  .dhk8-tko-stat__num {
    font-family: var(--font-display, system-ui); font-weight: 700;
    font-size: var(--pos-text-xl, 20px); color: var(--pos-fg, #E8EFE9);
    font-variant-numeric: tabular-nums;
  }
  .dhk8-tko-stat__unit {
    font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82);
    text-transform: lowercase;
  }

  /* ── Toolbar ─────────────────────────────────────────────────────────── */
  .dhk8-tko-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .dhk8-tko-hint { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tko-refresh {
    background: transparent; color: var(--pos-fg-secondary, #B6BFB8);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: 7px 12px; font: inherit; font-size: var(--pos-text-xs, 12px); cursor: pointer;
    display: inline-flex; align-items: center; gap: 5px;
  }
  .dhk8-tko-refresh:hover { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-tint, #1F8B3A); }

  /* Toggle Cola / Todos. Sin radius (regla NintaiGO). */
  .dhk8-tko-seg { display: inline-flex; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-tko-seg__btn { background: transparent; color: var(--pos-fg-tertiary, #7E8C82); border: none; border-right: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); padding: 8px 14px; font: inherit; font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600); cursor: pointer; min-height: var(--pos-touch-target, 44px); }
  .dhk8-tko-seg__btn:last-child { border-right: none; }
  .dhk8-tko-seg__btn:hover { color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-tko-seg__btn.is-on { background: var(--pos-tint, #1F8B3A); color: var(--pos-on-tint, #fff); }

  /* Badge de estatus (vista Todos). */
  .dhk8-tko-badge { display: inline-flex; align-items: center; font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600); padding: 3px 8px; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-tko-badge--open   { color: var(--pos-tint-on, #48C76C); border-color: color-mix(in oklch, var(--pos-tint, #1F8B3A) 40%, transparent); background: var(--pos-tint-soft, rgba(31,139,58,.16)); }
  .dhk8-tko-badge--wait   { color: var(--pos-warning, #E0A23C); border-color: color-mix(in oklch, var(--pos-warning, #E0A23C) 40%, transparent); background: var(--pos-warning-bg, rgba(224,162,60,.14)); }
  .dhk8-tko-badge--done   { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-separator-strong, rgba(255,255,255,.20)); }
  .dhk8-tko-badge--danger { color: var(--pos-danger, #E5484D); border-color: color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }
  .dhk8-tko-badge--muted  { color: var(--pos-fg-tertiary, #7E8C82); }

  /* ── States ──────────────────────────────────────────────────────────── */
  .dhk8-tko-err {
    color: var(--pos-danger, #ef4444); font-size: var(--pos-text-sm, 13px);
    padding: 10px 12px; border: 1px solid color-mix(in oklch, var(--pos-danger, #ef4444) 35%, transparent);
    background: var(--pos-danger-bg, rgba(239,68,68,.10));
  }
  .dhk8-tko-empty {
    color: var(--pos-fg-tertiary, #7E8C82); font-size: var(--pos-text-sm, 13px);
    padding: 36px 16px; text-align: center; font-style: italic;
  }
  .dhk8-tko-empty--zen {
    display: flex; flex-direction: column; align-items: center; gap: var(--pos-space-2, 8px);
    background: var(--pos-surface, #161C18); border: 1px dashed var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: 48px 16px;
  }
  .dhk8-tko-empty__icon { font-size: 28px; }

  /* ── QUEUE ───────────────────────────────────────────────────────────── */
  .dhk8-tko-queue {
    list-style: none; margin: 0; padding: 0;
    display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
    counter-reset: tko-queue;
  }
  .dhk8-tko-ticket {
    display: grid; grid-template-columns: 72px 1fr; gap: 0;
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    overflow: hidden;
    position: relative;
  }
  /* Tier coloring on the LEFT rail only — keeps the look distinct from
     /floor (no full-card status fill). */
  .dhk8-tko-ticket--fresh .dhk8-tko-ticket__rail { background: color-mix(in oklch, var(--pos-tint, #1F8B3A) 22%, var(--pos-elevated, #1E2520)); }
  .dhk8-tko-ticket--wait  .dhk8-tko-ticket__rail { background: color-mix(in oklch, var(--pos-warning, #E0A23C) 25%, var(--pos-elevated, #1E2520)); }
  .dhk8-tko-ticket--long  .dhk8-tko-ticket__rail { background: color-mix(in oklch, var(--pos-danger, #E5484D) 28%, var(--pos-elevated, #1E2520)); }

  .dhk8-tko-ticket__rail {
    display: flex; flex-direction: column; align-items: center; padding: var(--pos-space-4, 16px) 0;
    color: var(--pos-fg, #E8EFE9);
    border-right: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    position: relative;
  }
  .dhk8-tko-ticket__turn {
    font-family: var(--font-display, system-ui); font-weight: 800;
    font-size: var(--pos-text-2xl, 24px); line-height: 1;
    font-variant-numeric: tabular-nums;
    color: var(--pos-fg, #E8EFE9);
  }
  /* Subtle vertical thread — suggests "queue line". Square ends. */
  .dhk8-tko-ticket__rail-line {
    flex: 1; width: 2px; min-height: 14px;
    background: color-mix(in oklch, var(--pos-fg-tertiary, #7E8C82) 35%, transparent);
    margin-top: 8px;
  }

  .dhk8-tko-ticket__body { padding: var(--pos-space-3, 12px) var(--pos-space-4, 16px); display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); min-width: 0; }
  .dhk8-tko-ticket__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  .dhk8-tko-ticket__head-left { display: flex; align-items: center; gap: 10px; min-width: 0; flex-wrap: wrap; }
  .dhk8-tko-ticket__folio {
    font-family: var(--font-mono, ui-monospace); font-size: var(--pos-text-md, 15px);
    color: var(--pos-fg, #E8EFE9); font-weight: var(--pos-weight-semibold, 600);
  }
  /* Wait chip — rectangular, no pill radius. */
  .dhk8-tko-ticket__wait {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600);
    padding: 3px 8px;
  }
  .dhk8-tko-ticket__wait--fresh { color: var(--pos-tint-on, #48C76C); background: var(--pos-tint-soft, rgba(31,139,58,.16)); }
  .dhk8-tko-ticket__wait--wait  { color: var(--pos-warning, #E0A23C); background: var(--pos-warning-bg, rgba(224,162,60,.18)); }
  .dhk8-tko-ticket__wait--long  { color: var(--pos-danger, #E5484D);  background: var(--pos-danger-bg, rgba(229,72,77,.18)); }
  .dhk8-tko-ticket__when { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-tko-items { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .dhk8-tko-item { display: flex; align-items: baseline; gap: 8px; font-size: var(--pos-text-sm, 13px); color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-tko-item__qty { color: var(--pos-tint-on, #48C76C); font-weight: 700; flex-shrink: 0; font-variant-numeric: tabular-nums; }
  .dhk8-tko-item__name { flex: 1; min-width: 0; }
  .dhk8-tko-item__mods, .dhk8-tko-item__note { color: var(--pos-fg-tertiary, #7E8C82); font-size: var(--pos-text-xs, 12px); }
  .dhk8-tko-item__price { color: var(--pos-fg, #E8EFE9); flex-shrink: 0; font-variant-numeric: tabular-nums; }

  .dhk8-tko-ticket__foot {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    border-top: 1px dashed var(--pos-separator, rgba(255,255,255,.10));
    padding-top: var(--pos-space-3, 12px); margin-top: 2px;
  }
  .dhk8-tko-total { display: flex; flex-direction: column; gap: 1px; }
  .dhk8-tko-total__break { font-size: var(--pos-text-xxs, 11px); color: var(--pos-fg-tertiary, #7E8C82); font-variant-numeric: tabular-nums; }
  .dhk8-tko-total__label { font-size: var(--pos-text-xxs, 11px); color: var(--pos-fg-tertiary, #7E8C82); text-transform: uppercase; letter-spacing: .04em; }
  .dhk8-tko-total__amount { font-size: var(--pos-text-xl, 20px); color: var(--pos-fg, #E8EFE9); font-variant-numeric: tabular-nums; font-weight: var(--pos-weight-semibold, 600); }

  /* Botones del pie del boleto: Editar · Anular · Pagado (sin radius/sombra). */
  .dhk8-tko-ticket__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .dhk8-tko-secondary {
    background: transparent; color: var(--pos-fg-secondary, #B6BFB8);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: 10px 12px; font: inherit; font-size: var(--pos-text-sm, 13px);
    font-weight: var(--pos-weight-medium, 500); cursor: pointer;
    display: inline-flex; align-items: center; gap: 5px;
    min-height: var(--pos-touch-target, 44px);
  }
  .dhk8-tko-secondary:hover:not(:disabled) { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tko-secondary:disabled { opacity: .55; cursor: not-allowed; }
  .dhk8-tko-secondary--danger { color: var(--pos-danger, #E5484D); border-color: color-mix(in oklch, var(--pos-danger, #E5484D) 35%, transparent); }
  .dhk8-tko-secondary--danger:hover:not(:disabled) { color: var(--pos-danger, #E5484D); border-color: var(--pos-danger, #E5484D); background: var(--pos-danger-bg, rgba(229,72,77,.10)); }

  .dhk8-tko-pay {
    background: var(--pos-tint, #1F8B3A); color: #fff;
    border: 1px solid var(--pos-tint, #1F8B3A);
    padding: 10px 14px; font: inherit; font-size: var(--pos-text-sm, 13px);
    font-weight: var(--pos-weight-semibold, 600); cursor: pointer;
    display: inline-flex; align-items: center; gap: 4px;
    min-height: var(--pos-touch-target, 44px);
  }
  .dhk8-tko-pay:hover:not(:disabled) { filter: brightness(1.08); }
  .dhk8-tko-pay:active:not(:disabled) { filter: brightness(.92); }
  .dhk8-tko-pay:disabled { opacity: .55; cursor: not-allowed; }

  @media (max-width: 560px) {
    .dhk8-tko-ticket { grid-template-columns: 56px 1fr; }
    .dhk8-tko-ticket__turn { font-size: var(--pos-text-lg, 17px); }
  }
`
